import React from 'react';
import { BarChart3Icon, DownloadIcon, FileTextIcon, PieChartIcon } from 'lucide-react';
interface ReportsPageProps {
  t: (key: string) => string;
  lands: any[];
}
export function ReportsPage({
  t,
  lands
}: ReportsPageProps) {
  const totalArea = lands.reduce((acc, land) => {
    const val = parseFloat(land.area.split(' ')[0]);
    return acc + (isNaN(val) ? 0 : val);
  }, 0);
  const agriculturalCount = lands.filter(l => l.type === 'Agricultural').length;
  const residentialCount = lands.filter(l => l.type === 'Residential').length;
  const commercialCount = lands.filter(l => l.type === 'Commercial').length;
  return <div className="space-y-6 pb-20 lg:pb-6" data-id="element-675">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-676">
        <div data-id="element-677">
          <h2 className="text-2xl font-bold text-slate-800" data-id="element-678">
            {t('reports.title')}
          </h2>
          <p className="text-slate-500" data-id="element-679">{t('reports.subtitle')}</p>
        </div>
        <button className="bg-[#006A4E] text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-[#00523c] transition-colors flex items-center gap-2 shadow-sm" data-id="element-680">
          <DownloadIcon className="w-4 h-4" data-id="element-681" /> {t('reports.generate')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-682">
        {/* Tax Summary Report */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-id="element-683">
          <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4" data-id="element-684">
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center" data-id="element-685">
              <BarChart3Icon className="w-5 h-5" data-id="element-686" />
            </div>
            <h3 className="text-lg font-bold text-slate-800" data-id="element-687">
              {t('reports.taxSummary')}
            </h3>
          </div>

          <div className="space-y-4" data-id="element-688">
            <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg" data-id="element-689">
              <span className="text-slate-600 font-medium" data-id="element-690">Total Lands</span>
              <span className="font-bold text-slate-900" data-id="element-691">{lands.length}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg" data-id="element-692">
              <span className="text-green-700 font-medium" data-id="element-693">Tax Paid</span>
              <span className="font-bold text-green-700" data-id="element-694">
                {lands.filter(l => l.tax === 'Paid').length}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg" data-id="element-695">
              <span className="text-amber-700 font-medium" data-id="element-696">Tax Due</span>
              <span className="font-bold text-amber-700" data-id="element-697">
                {lands.filter(l => l.tax === 'Due').length}
              </span>
            </div>
          </div>
        </div>

        {/* Land Distribution Report */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-id="element-698">
          <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4" data-id="element-699">
            <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-lg flex items-center justify-center" data-id="element-700">
              <PieChartIcon className="w-5 h-5" data-id="element-701" />
            </div>
            <h3 className="text-lg font-bold text-slate-800" data-id="element-702">
              {t('reports.landDistribution')}
            </h3>
          </div>

          <div className="space-y-4" data-id="element-703">
            <div className="flex items-center gap-4" data-id="element-704">
              <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden flex" data-id="element-705">
                <div style={{
                width: `${agriculturalCount / lands.length * 100}%`
              }} className="bg-green-500 h-full" data-id="element-706"></div>
                <div style={{
                width: `${residentialCount / lands.length * 100}%`
              }} className="bg-blue-500 h-full" data-id="element-707"></div>
                <div style={{
                width: `${commercialCount / lands.length * 100}%`
              }} className="bg-purple-500 h-full" data-id="element-708"></div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center pt-2" data-id="element-709">
              <div data-id="element-710">
                <div className="w-3 h-3 bg-green-500 rounded-full mx-auto mb-1" data-id="element-711"></div>
                <p className="text-xs text-slate-500" data-id="element-712">
                  {t('land.agricultural')}
                </p>
                <p className="font-bold" data-id="element-713">{agriculturalCount}</p>
              </div>
              <div data-id="element-714">
                <div className="w-3 h-3 bg-blue-500 rounded-full mx-auto mb-1" data-id="element-715"></div>
                <p className="text-xs text-slate-500" data-id="element-716">
                  {t('land.residential')}
                </p>
                <p className="font-bold" data-id="element-717">{residentialCount}</p>
              </div>
              <div data-id="element-718">
                <div className="w-3 h-3 bg-purple-500 rounded-full mx-auto mb-1" data-id="element-719"></div>
                <p className="text-xs text-slate-500" data-id="element-720">{t('land.commercial')}</p>
                <p className="font-bold" data-id="element-721">{commercialCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity Report */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 md:col-span-2" data-id="element-722">
          <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-4" data-id="element-723">
            <div className="w-10 h-10 bg-slate-100 text-slate-600 rounded-lg flex items-center justify-center" data-id="element-724">
              <FileTextIcon className="w-5 h-5" data-id="element-725" />
            </div>
            <h3 className="text-lg font-bold text-slate-800" data-id="element-726">
              Recent Activity Log
            </h3>
          </div>

          <div className="overflow-x-auto" data-id="element-727">
            <table className="w-full text-left text-sm" data-id="element-728">
              <thead className="bg-slate-50 text-slate-600" data-id="element-729">
                <tr data-id="element-730">
                  <th className="py-3 px-4" data-id="element-731">Date</th>
                  <th className="py-3 px-4" data-id="element-732">Action</th>
                  <th className="py-3 px-4" data-id="element-733">Reference</th>
                  <th className="py-3 px-4" data-id="element-734">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-735">
                <tr data-id="element-736">
                  <td className="py-3 px-4" data-id="element-737">15 Jan 2026</td>
                  <td className="py-3 px-4" data-id="element-738">Certificate Generated</td>
                  <td className="py-3 px-4 font-mono" data-id="element-739">LOC-2026-14589</td>
                  <td className="py-3 px-4" data-id="element-740">
                    <span className="text-green-600 font-medium" data-id="element-741">Success</span>
                  </td>
                </tr>
                <tr data-id="element-742">
                  <td className="py-3 px-4" data-id="element-743">10 Jan 2026</td>
                  <td className="py-3 px-4" data-id="element-744">Tax Payment</td>
                  <td className="py-3 px-4 font-mono" data-id="element-745">LAND-001</td>
                  <td className="py-3 px-4" data-id="element-746">
                    <span className="text-green-600 font-medium" data-id="element-747">Success</span>
                  </td>
                </tr>
                <tr data-id="element-748">
                  <td className="py-3 px-4" data-id="element-749">05 Jan 2026</td>
                  <td className="py-3 px-4" data-id="element-750">Mutation Application</td>
                  <td className="py-3 px-4 font-mono" data-id="element-751">LAND-002</td>
                  <td className="py-3 px-4" data-id="element-752">
                    <span className="text-amber-600 font-medium" data-id="element-753">Pending</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}